import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { type ReactNode } from "react";
import { ArrowLeft, BadgeCheck } from "lucide-react";
import { getKbArticle, listKbArticles } from "@/lib/kb.functions";
import { buildTitle, clampDescription } from "@/lib/seo-quality";
import { ReadingProgress } from "@/components/ReadingProgress";
import { TableOfContents } from "@/components/TableOfContents";
import { ShareButtons } from "@/components/ShareButtons";
import { NewsletterSignup } from "@/components/NewsletterSignup";
import { KbArticleBody } from "@/components/kb/KbArticleBody";
import { getFyndanuListings, type FyndanuListing } from "@/lib/fyndanu.functions";
import { imgSrcSet, HERO_WIDTHS, HERO_SIZES } from "@/lib/img";
import { autoInternalLinks, type LinkTarget } from "@/lib/auto-internal-links";
import { RelatedLinks } from "@/components/RelatedLinks";
import { AdSlot } from "@/components/AdSlot";

const SITE = "https://www.akhusbilfritid.se";

type Faq = { q: string; a: string };

export const Route = createFileRoute("/kunskap/$slug")({
  loader: async ({ params }) => {
    const { article } = await getKbArticle({ data: { slug: params.slug } });
    if (!article) throw notFound();
    const { articles } = await listKbArticles().catch(() => ({ articles: [] as any[] }));
    const related = (articles ?? [])
      .filter((a: any) => a.slug !== params.slug && a.cluster === (article as any).cluster)
      .slice(0, 3);
    const { items } = await getFyndanuListings().catch(() => ({ items: [] as FyndanuListing[] }));
    // Kontextuell internlänkning i löptexten: andra kunskapsartiklar + fasta hubbar.
    const linkedArticle = {
      ...(article as any),
      content: autoInternalLinks(
        ((article as any).content ?? "") as string,
        (articles ?? []) as LinkTarget[],
        {
          currentSlug: params.slug,
          postBasePath: "/kunskap",
          maxLinks: 12,
          maxPerParagraph: 2,
        },
      ),
    };
    return { article: linkedArticle, related, listings: items ?? [] };
  },
  head: ({ loaderData }) => {
    const a = loaderData?.article as any;
    if (!a) return {};
    const title = buildTitle(a.title);
    const description = clampDescription(a.meta_description ?? a.excerpt ?? a.title);
    const url = `${SITE}/kunskap/${a.slug}`;
    const faq: Faq[] = Array.isArray(a.faq) ? a.faq : [];
    const graph: unknown[] = [
      {
        "@type": "Article",
        headline: a.title,
        description,
        datePublished: a.published_at,
        dateModified: a.updated_at ?? a.published_at,
        mainEntityOfPage: { "@type": "WebPage", "@id": url },
        author: {
          "@type": "Organization",
          name: "AK Husbil & Fritid",
          url: `${SITE}/om-oss`,
        },
        publisher: { "@type": "Organization", name: "AK Husbil & Fritid", url: SITE },
        ...(a.cover_url ? { image: [a.cover_url] } : {}),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Hem", item: SITE },
          { "@type": "ListItem", position: 2, name: "Kunskapsbank", item: `${SITE}/kunskap` },
          { "@type": "ListItem", position: 3, name: a.title, item: url },
        ],
      },
    ];
    if (faq.length) {
      graph.push({
        "@type": "FAQPage",
        mainEntity: faq.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      });
    }
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
        ...(a.cover_url
          ? [
              { property: "og:image", content: a.cover_url as string },
              { name: "twitter:image", content: a.cover_url as string },
            ]
          : []),
        { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({ "@context": "https://schema.org", "@graph": graph }),
        },
      ],
    };
  },
  component: KunskapArticle,
});

function KunskapArticle() {
  const { article, related, listings } = Route.useLoaderData() as {
    article: any;
    related: any[];
    listings: FyndanuListing[];
  };
  const faq: Faq[] = Array.isArray(article.faq) ? article.faq : [];
  const tldr: string[] = Array.isArray(article.tldr) ? article.tldr : [];
  

  return (
    <>
      <ReadingProgress />
      <article className="container-edge max-w-6xl py-10 md:py-16">
        <Link to="/kunskap" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-accent">
          <ArrowLeft size={15} /> Kunskapsbanken
        </Link>

        <header className="mt-6 max-w-3xl">
          {article.cluster && <p className="eyebrow">{article.cluster}</p>}
          <h1 className="mt-3 font-display text-3xl leading-tight md:text-5xl">{article.title}</h1>
          {article.excerpt && (
            <p className="mt-5 text-base text-muted-foreground md:text-lg">{article.excerpt}</p>
          )}
          <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <BadgeCheck size={14} className="text-accent" /> Faktagranskad av verkstaden i Kalix
            </span>
            <span>{article.reading_minutes ?? 7} min läsning</span>
            {article.published_at && (
              <span>
                Publicerad{" "}
                {new Date(article.published_at).toLocaleDateString("sv-SE", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </span>
            )}
          </div>
        </header>

        {article.cover_url && (
          <img
            src={article.cover_url}
            srcSet={imgSrcSet(article.cover_url, HERO_WIDTHS)}
            sizes={HERO_SIZES}
            alt={article.title}
            width={1600}
            height={900}
            fetchPriority="high"
            decoding="async"
            className="mt-8 aspect-[16/9] w-full rounded-3xl border border-border object-cover"
          />

        )}

        <div className="mt-10 grid gap-10 lg:grid-cols-[minmax(0,1fr)_260px]">
          <div className="min-w-0">
            {tldr.length > 0 && (
              <div className="mb-8 rounded-3xl border border-accent/30 bg-accent/5 p-5 md:p-6">
                <p className="eyebrow">Snabbversionen</p>
                <ul className="mt-3 space-y-2 text-sm">
                  {tldr.map((t, i) => (
                    <li key={i} className="flex gap-2">
                      <span className="text-accent">•</span>
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <KbArticleBody
              slug={article.slug}
              keyword={article.focus_keyword ?? article.cluster}
              content={article.content}
              listings={listings ?? []}
            />

            <AdSlot variant="in-article" />

            {faq.length > 0 && (
              <section className="mt-14">
                <h2 className="font-display text-2xl md:text-3xl">Vanliga frågor</h2>
                <div className="mt-6 divide-y divide-border overflow-hidden rounded-3xl border border-border bg-card">
                  {faq.map((f, i) => (
                    <details key={i} className="group p-5">
                      <summary className="cursor-pointer font-display text-lg marker:hidden">{f.q}</summary>
                      <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
                    </details>
                  ))}
                </div>
              </section>
            )}

            <RelatedLinks
              keyId={article.slug}
              text={`${article.title} ${article.focus_keyword ?? ""} ${article.cluster ?? ""} ${String(article.content ?? "").slice(0, 4000)}`}
              currentHref={`/kunskap/${article.slug}`}
            />

            <AdSlot variant="multiplex" label="Relaterat innehåll" />

            <div className="mt-12">
              <ShareButtons path={`/kunskap/${article.slug}`} title={article.title} />
            </div>


            <div className="mt-12">
              <NewsletterSignup />
            </div>
          </div>

          <aside className="hidden lg:block">
            <div className="sticky top-24 space-y-6">
              <TableOfContents markdown={article.content} />
              {related.length > 0 && (
                <div className="rounded-3xl border border-border bg-card p-5">
                  <p className="eyebrow">Läs vidare</p>
                  <ul className="mt-3 space-y-3 text-sm">
                    {related.map((r) => (
                      <li key={r.id}>
                        <Link
                          to="/kunskap/$slug"
                          params={{ slug: r.slug }}
                          className="hover:text-accent"
                        >
                          {r.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </aside>
        </div>
      </article>
    </>
  );
}
